/* eslint-disable max-len */
import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import dayjs from 'dayjs';

import Loading from '../components/Loading';
import { currencyFormatter } from '../utils/formatters';
import AppContext from '../context';
import APIWrapper from '../utils/graphqlwrapper';
import FallbackOnError from '../components/FallbackOnError';
import { logReactErrBoundaryError } from '../utils';
import logger from '../utils/logger';
import CodesCard from '../components/CodesCard';
import DataRecencyCard from '../components/DataRecencyCard';
import ActiveStatusCard from '../components/ActiveStatusCard';

import GoopLogo from '../assets/goop-logo.png';
import TotemLogo from '../assets/totem-logo.png';
import SBSOverview from './reporting/SBSOverview';

function ClientLandingPage(props) {
  const [codesCount, setCodesCount] = useState();
  const [todaysRevenue, setTodaysRevenue] = useState();
  const [monthsRevenue, setMonthsRevenue] = useState();
  const [isRevenueLoading, setIsLoadingRevenue] = useState(true);
  const [updatedDateTime, setUpdatedDateTime] = useState('');
  const [updateDataRecency, setUpdateDataRecency] = useState([]);
  const [clientInfo, setClientInfo] = useState({});
  const [goopIds, setGoopIds] = useState([]);
  const [totemIds, setTotemIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setShouldLogOut } = useContext(AppContext);
  const param = useParams();
  const navigate = useNavigate();
  const { hasTDCAdminPermission, hasDataEngineerPermission, hasAnalystPermission } = props;
  const startDate = dayjs().startOf('month').format('MM/DD');
  const endDate = dayjs().format('MM/DD');
  let thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30)); // -30 days from now
  thirtyDaysAgo = new Date(thirtyDaysAgo.setHours(0, 0, 0, 0));

  const handleData = (data, updatedAt) => {
    // set revenue data once we get it back from the SBSOverview component
    const parsedData = JSON.parse(data);
    const today = dayjs().format('MM/DD/YY');
    if (parsedData.report && parsedData.report[today]) {
      parsedData.report[today].overallRevenue = parsedData.report[today].overallRevenue || 0;
      setTodaysRevenue(currencyFormatter(parsedData.report[today].overallRevenue));
    } else {
      setTodaysRevenue(currencyFormatter(0));
    }
    setMonthsRevenue(currencyFormatter(parsedData.revenue));
    setUpdatedDateTime(updatedAt);
    setIsLoadingRevenue(false);
  };

  const codesSearchObj = {
    client_id: { eq: param.client },
    and: {
      most_recent_transaction_date: { gte: thirtyDaysAgo.toISOString() },
      and: {
        source: { exists: 'false' },
      },
    },
  };

  useEffect(() => {
    const fetchClientInfo = async () => {
      /**
       * Get client information
       */
      let results;
      try {
        results = await APIWrapper.queryApi(
          { query: 'getClient' },
          setShouldLogOut,
          { client_id: param.client },
        );

        // if we have client data returned then let's set goop and totem ids to state if the  values are not null
        if (results && results.data[0].length !== 0) {
          if (results.data[0][0].goop_client_ids !== null) {
            setGoopIds(results.data[0][0].goop_client_ids);
          } if (results.data[0][0].totem_client_ids !== null) {
            setTotemIds(results.data[0][0].totem_client_ids);
          }
        }
      } catch (err) {
        logger.error(err);
      }
      return results.data[0][0];
    };

    const fetchCodesCount = async () => {
      /**
       * Get the count of ungrouped codes from the last year
       */
      let results;
      try {
        results = await APIWrapper.queryApi(
          { query: 'searchCodes' },
          setShouldLogOut,
          {
            filter: codesSearchObj,
            limit: 1,
          },
        );
      } catch (err) {
        logger.error(err);
      }
      return results.total;
    };

    const databricksTableDataFetch = async () => {
      /**
       * Get the latest data from the prod.pipeline_monitoring.most_recent_table_dates_by_client datalake table
       */

      let results;
      try {
        results = await APIWrapper.queryApi(
          { query: 'databricksTableDataFetch' },
          setShouldLogOut,
          { queryType: 'ClientLanding', clients: [param.client] },
        );
      } catch (err) {
        logger.error(err);
      }
      if (results && results.data[0].length === 0) {
        // if no data return empty array so there aren't any errors when we map through this data
        return [];
      }
      return JSON.parse(results.data[0][0]);
    };

    setIsLoadingRevenue(true);
    Promise.all([
      fetchClientInfo().then((data) => setClientInfo(data)),
      fetchCodesCount().then((data) => setCodesCount(data)),
      databricksTableDataFetch().then((data) => setUpdateDataRecency(data[0])),
    ]).finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    document.title = `MissionPortal | ${param.client}`;
  }, []);

  const handleSettingsClick = () => {
    navigate(`/client/${param.client}/settings`, {
      replace: false,
      state: { client: param.client },
    });
  };

  return (
    <ErrorBoundary
      FallbackComponent={FallbackOnError}
      onError={logReactErrBoundaryError}
    >
      {isLoading
        ? (<Loading fullPage />)
        : (
          <>
            <div className="landing-header">
              {(hasTDCAdminPermission || hasDataEngineerPermission || hasAnalystPermission)
                && <Button variant="contained" onClick={() => handleSettingsClick()}>Client Settings</Button>}
            </div>
            <h3 className="section-header-text">Overview</h3>
            <div className="codes-rev-container">
              <CodesCard topText="Ungrouped codes" count={codesCount} includeTooltip toolTipText="Go to Codes" bottomText="in the past 30 days" />
              <CodesCard topText="Raised Today" revenue={todaysRevenue} isLoading={isRevenueLoading} bottomText={`Last updated: ${updatedDateTime}`} />
              <CodesCard topText="Raised this Month" revenue={monthsRevenue} isLoading={isRevenueLoading} bottomText={`Last updated: ${updatedDateTime}`} />
            </div>
            <div className="section-header-text revenue-header">
              <h3>
                {' '}
                Revenue Totals -
                {' '}
                {startDate}
                {' '}
                to
                {' '}
                {endDate}
              </h3>
              <Button>
                <Tooltip title="To see data for a custom period of time please use the export page or click here" placement="bottom">
                  <Link to={`/client/${param.client}/Strategy/Multi-Channel/SBS/Download`}>
                    <InfoIcon sx={{ fill: '#5e6262' }} />
                  </Link>
                </Tooltip>
              </Button>
            </div>
            <div className="revenue-total-container">
              <SBSOverview onData={handleData} />
            </div>
            <h3 className="section-header-text">Data Recency (Last Update)</h3>
            <div className="data-recency-container">
              <DataRecencyCard platform="Datalake" timestamp={updatedDateTime} />
              {updateDataRecency && updateDataRecency
                .filter((data) => clientInfo.transaction_platform.includes(data.platform))
                .map((data) => (
                  <DataRecencyCard key={data.platform} platform={data.platform} lastRefresh={data.days_since} timestamp={moment(data.last_date).tz('America/New_York').format('MM/DD/YYYY hh:mma z')} />
                ))}
            </div>
            <h3 className="section-header-text">Active Status Summary</h3>
            <div className="active-status-container">
              <ActiveStatusCard isActive={clientInfo.is_active ? 'Yes' : 'No'} isAdAiMem={clientInfo.is_advantage_member ? 'Yes' : 'No'} pauseData={clientInfo.pause_data} />
            </div>
            {goopIds.length === 0 && totemIds.length === 0 ? '' : <h3 className="section-header-text">Other Apps</h3>}
            <div className="goop-totem-links-container">
              {goopIds.map((data) => (
                <div key={data} className="goop-links">
                  <Tooltip title={`Go to Goop client: ${data}`} placement="bottom">
                    <Link to={`https://goop.missionwired.com/#/client/${data}/groupings`} target="_blank">
                      <img src={GoopLogo} alt="Goop logo" />
                    </Link>
                  </Tooltip>
                  <h4>
                    {`Goop ID: ${data}`}
                  </h4>
                </div>
              ))}
              {totemIds.map((data) => (
                <div key={data} className="totem-links">
                  <Tooltip title={`Go to Totem client: ${data}`} placement="bottom">
                    <Link to={`https://totem.missionwired.com/#!/home/${data}`} target="_blank">
                      <img src={TotemLogo} alt="Totem logo" />
                    </Link>
                  </Tooltip>
                  <h4>
                    {`Totem ID: ${data}`}
                  </h4>
                </div>
              ))}
            </div>
          </>
        )}
    </ErrorBoundary>
  );
}

export default ClientLandingPage;

ClientLandingPage.propTypes = {
  hasTDCAdminPermission: PropTypes.bool.isRequired,
  hasDataEngineerPermission: PropTypes.bool.isRequired,
  hasAnalystPermission: PropTypes.bool.isRequired,
};
